<!-- components/ui/Pagination.vue -->
<template>
  <nav
    class="flex items-center justify-center gap-4 select-none"
    role="navigation"
    aria-label="Pagination"
  >
    <!-- Prev -->
    <button
      class="md:w-14 md:h-14 w-10 h-10 rounded-full flex items-center justify-center transition shadow-sm disabled:bg-[#E9EBEF] disabled:text-[#A9B3C1] bg-[#0057FF] text-white hover:brightness-95"
      :disabled="currentPage <= 1"
      :class="{ 'pointer-events-none': currentPage <= 1 }"
      @click="go(currentPage - 1)"
      aria-label="Anterior"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="56"
        height="56"
        viewBox="0 0 56 56"
        fill="none"
      >
        <path
          d="M27.44 30.8H36.4C37.1933 30.8 37.8583 30.5317 38.395 29.995C38.9317 29.4583 39.2 28.7933 39.2 28C39.2 27.2067 38.9317 26.5417 38.395 26.005C37.8583 25.4683 37.1933 25.2 36.4 25.2H27.44L29.96 22.68C30.4733 22.1667 30.73 21.5133 30.73 20.72C30.73 19.9267 30.4733 19.2733 29.96 18.76C29.4467 18.2467 28.7933 17.99 28 17.99C27.2067 17.99 26.5533 18.2467 26.04 18.76L18.76 26.04C18.2 26.6 17.92 27.2533 17.92 28C17.92 28.7467 18.2 29.4 18.76 29.96L26.04 37.24C26.5533 37.7533 27.2067 38.01 28 38.01C28.7933 38.01 29.4467 37.7533 29.96 37.24C30.4733 36.7267 30.73 36.0733 30.73 35.28C30.73 34.4867 30.4733 33.8333 29.96 33.32L27.44 30.8ZM28 56C24.1267 56 20.4867 55.265 17.08 53.795C13.6733 52.325 10.71 50.33 8.19 47.81C5.67 45.29 3.675 42.3267 2.205 38.92C0.735 35.5133 0 31.8733 0 28C0 24.1267 0.735 20.4867 2.205 17.08C3.675 13.6733 5.67 10.71 8.19 8.19C10.71 5.67 13.6733 3.675 17.08 2.205C20.4867 0.735 24.1267 0 28 0C31.8733 0 35.5133 0.735 38.92 2.205C42.3267 3.675 45.29 5.67 47.81 8.19C50.33 10.71 52.325 13.6733 53.795 17.08C55.265 20.4867 56 24.1267 56 28C56 31.8733 55.265 35.5133 53.795 38.92C52.325 42.3267 50.33 45.29 47.81 47.81C45.29 50.33 42.3267 52.325 38.92 53.795C35.5133 55.265 31.8733 56 28 56Z"
          fill="#E7E7E7"
        />
        <path
          d="M27.44 30.8H36.4C37.1933 30.8 37.8583 30.5317 38.395 29.995C38.9317 29.4583 39.2 28.7933 39.2 28C39.2 27.2067 38.9317 26.5417 38.395 26.005C37.8583 25.4683 37.1933 25.2 36.4 25.2H27.44L29.96 22.68C30.4733 22.1667 30.73 21.5133 30.73 20.72C30.73 19.9267 30.4733 19.2733 29.96 18.76C29.4467 18.2467 28.7933 17.99 28 17.99C27.2067 17.99 26.5533 18.2467 26.04 18.76L18.76 26.04C18.2 26.6 17.92 27.2533 17.92 28C17.92 28.7467 18.2 29.4 18.76 29.96L26.04 37.24C26.5533 37.7533 27.2067 38.01 28 38.01C28.7933 38.01 29.4467 37.7533 29.96 37.24C30.4733 36.7267 30.73 36.0733 30.73 35.28C30.73 34.4867 30.4733 33.8333 29.96 33.32L27.44 30.8Z"
          fill="#B9B9B9"
        />
      </svg>
    </button>

    <!-- Numbers -->
    <ul class="flex items-center gap-6">
      <li v-for="n in pageWindow" :key="n">
        <button
          class="text-sm md:text-xl transition font-poppins font-bold"
          :class="
            n === currentPage
              ? 'text-[#0554F2]'
              : 'text-[#586B84] hover:text-[#0554F2]'
          "
          @click="go(n)"
          :aria-current="n === currentPage ? 'page' : undefined"
        >
          {{ n }}
        </button>
      </li>
    </ul>

    <!-- Next -->
    <button
      class="md:w-14 md:h-14 w-10 h-10 cursor-pointer rounded-full flex items-center justify-center transition shadow-sm disabled:bg-[#E9EBEF] disabled:text-[#A9B3C1] bg-[#0057FF] text-white hover:brightness-95"
      :disabled="currentPage >= pageCount"
      @click="go(currentPage + 1)"
      :class="{ 'pointer-events-none': currentPage >= pageCount }"
      aria-label="Siguiente"
    >
      <!-- right arrow -->
      <img :src="rightButton" alt="arrow" />
    </button>
  </nav>
</template>

<script setup lang="ts">
import rightButton from "@/assets/img/icon/right-arrow.svg";

const props = defineProps<{
  currentPage: number;
  pageCount: number;
  window?: number; // how many numbers visible (default 5)
}>();

const emit = defineEmits<{ (e: "change", page: number): void }>();

const win = computed(() => Math.max(1, props.window ?? 5));

const pageWindow = computed(() => {
  const total = Math.max(1, props.pageCount);
  const cur = Math.min(Math.max(1, props.currentPage), total);
  const half = Math.floor(win.value / 2);

  let start = cur - half;
  let end = cur + half;

  if (win.value % 2 === 0) end -= 1; // keep count == window

  if (start < 1) {
    end += 1 - start;
    start = 1;
  }
  if (end > total) {
    start -= end - total;
    end = total;
  }
  start = Math.max(1, start);

  const arr: number[] = [];
  for (let i = start; i <= end && arr.length < win.value; i++) arr.push(i);
  return arr;
});

function go(p: number) {
  const clamped = Math.min(Math.max(1, p), Math.max(1, props.pageCount));
  if (clamped !== props.currentPage) emit("change", clamped);
}
</script>
